import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IItemForGetShortInfo, IGetPaginatedDishesForAssign, IDishesForAssign, IAssignmentsForAssignDish } from 'types';
import { IAppContext, AppContext } from 'appState/appContext';
import { dishAssignmentReducer } from './dishAssignmentReducer';
import { DishAssignmentActionTypes } from './dishAssignmentActions';
import {
	dishAssignmentInitialState,
	DishAssignmentContext,
	TActiveItemToDisplay,
	TTypeOfModalWindow,
	IIDsSelectedItems,
	IDishesWithAdditionalProperty,
	TSelectedItem,
	TDishesSortBy,
	IDishWithAdditionalField,
} from './dishAssignmentContext';
import {
	getPaginatedDishesForAssign,
	getCategoriesByIDRestaurantForAssignDishes,
	getSubcategoriesByIDsCategoriesForAssignDishes,
	getDishesByIDsSubcategoriesForAssignDishes,
	assignDish,
} from 'backend/dishes';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { createAppError } from 'utils/helpers';

const DishAssignmentState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(dishAssignmentReducer, dishAssignmentInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IDishesForAssign>, IGetPaginatedDishesForAssign>(
		state.IDSelectedRestaurant ? getPaginatedDishesForAssign : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			restaurantId: state.IDSelectedRestaurant ? state.IDSelectedRestaurant : '',
			priceFrom: state.price ? Number(state.price) : null,
			priceTo: state.price ? Number(state.price) : null,
			isUnassignedDishes: state.isUnassignedDishes ? true : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByPrice: state.sortBy === 'Price1-10' || state.sortBy === 'Price10-1' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: DishAssignmentActionTypes.SET_DISHES_FOR_ASSIGN,
				payload: {
					dishesForAssign: response.data,
				},
			});
		}
	}, [response]);

	const setCategories = async (restaurantId: string) => {
		try {
			showLoader();
			const response = await getCategoriesByIDRestaurantForAssignDishes(restaurantId);
			dispatch({
				type: DishAssignmentActionTypes.SET_CATEGORIES,
				payload: {
					categories: response.data.categories,
					notificationsForCategory: response.data.categories.length ? 'showNothing' : 'showNotification',
				},
			});
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const changeSearchStringForCategories = useCallback((value: string) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_CATEGORIES,
			payload: {
				searchStringForCategories: value,
			},
		});
	}, []);

	const changeSearchStringForSubcategories = useCallback((value: string) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_SUBCATEGORIES,
			payload: {
				searchStringForSubcategories: value,
			},
		});
	}, []);

	const changeSearchStringForDishes = useCallback((value: string) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_DISHES,
			payload: {
				searchStringForDishes: value,
			},
		});
	}, []);

	const setIDSelectedCustomer = (value: TSelectedItem) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_ID_SELECTED_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setFetchedRestaurants = (value: IItemForGetShortInfo[]) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	};

	const setIDSelectedRestaurant = (value: TSelectedItem) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_ID_SELECTED_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const setPrice = (value: string) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_PRICE,
			payload: {
				price: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: DishAssignmentActionTypes.RESET_SELECTED_CONTROLS });

	const changeIsUnassignedDishesStatus = (value: boolean) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_UNASSIGNED_DISHES_STATUS,
			payload: {
				isUnassignedDishes: value,
			},
		});
	};

	const setSortBy = (sortBy: TDishesSortBy) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setIDsSelectedDishes = (value: string[]) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_IDS_SELECTED_DISHES,
			payload: {
				IDsSelectedDishes: value,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDsSelectedCategories = async (value: IIDsSelectedItems, isUpdateSubcategoriesList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateSubcategoriesList) {
				dispatch({
					type: DishAssignmentActionTypes.SET_IDS_SELECTED_CATEGORIES,
					payload: {
						IDsSelectedCategories: {},
						subcategories: [],
						IDsSelectedSubcategories: {},
						dishes: [],
					},
				});
			} else if (isUpdateSubcategoriesList) {
				const arrWithCategoryIDs = Object.keys(value);
				showLoader();
				const response = await getSubcategoriesByIDsCategoriesForAssignDishes({
					restaurantId: state.IDSelectedRestaurant ? state.IDSelectedRestaurant : '',
					categoryIds: arrWithCategoryIDs,
				});
				const allSubcategoryIDs = response.data.categories
					.filter((category) => category.subCategories.length)
					.map((category) => category.subCategories.map((subcategory) => subcategory.id))
					.flat();
				const updatedIDsSelectedSubcategories = allSubcategoryIDs.reduce<IIDsSelectedItems>((acc, key) => {
					if (key in state.IDsSelectedSubcategories) {
						acc[key] = state.IDsSelectedSubcategories[key];
					}
					return acc;
				}, {});
				const updatedDishes = state.dishes.filter((el) => allSubcategoryIDs.includes(el.id));
				dispatch({
					type: DishAssignmentActionTypes.SET_IDS_SELECTED_CATEGORIES,
					payload: {
						IDsSelectedCategories: value,
						subcategories: response.data.categories,
						IDsSelectedSubcategories: updatedIDsSelectedSubcategories,
						dishes: updatedDishes,
					},
				});
			} else {
				// just to open and close tabs with subcategories.
				dispatch({
					type: DishAssignmentActionTypes.SET_IDS_SELECTED_CATEGORIES,
					payload: {
						IDsSelectedCategories: value,
						subcategories: state.subcategories,
						IDsSelectedSubcategories: state.IDsSelectedSubcategories,
						dishes: state.dishes,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const changeActiveItemToDisplay = (value: TActiveItemToDisplay) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_ACTIVE_ITEM_TO_DISPLAY,
			payload: {
				activeItemToDisplay: value,
			},
		});
	};

	const setShowBreadcrumbs = (value?: boolean) => {
		const isShowBreadCrumbs = value !== undefined ? value : !state.isShowBreadcrumbs;

		dispatch({
			type: DishAssignmentActionTypes.TOGGLE_SHOW_BREADCRUMBS,
			payload: {
				isShowBreadCrumbs,
			},
		});
	};

	const setIDsSelectedSubcategories = async (value: IIDsSelectedItems, isUpdateDishesList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateDishesList) {
				dispatch({
					type: DishAssignmentActionTypes.SET_IDS_SELECTED_SUBCATEGORIES,
					payload: {
						IDsSelectedSubcategories: {},
						dishes: [],
					},
				});
			} else if (isUpdateDishesList) {
				const arrWithSubcategoryIDs = Object.keys(value);
				showLoader();
				const response = await getDishesByIDsSubcategoriesForAssignDishes({
					restaurantId: state.IDSelectedRestaurant ? state.IDSelectedRestaurant : '',
					subCategoryIds: arrWithSubcategoryIDs,
				});
				const IDsSubcategoriesFromState = state.dishes.map((el) => el.id);
				const IDsSubcategoriesFromResponse = response.data.subCategories.map((el) => el.id);
				const filteredDishesFromState = state.dishes.filter((el) =>
					IDsSubcategoriesFromResponse.includes(el.id),
				);
				const filteredDishesFromResponse = response.data.subCategories
					.filter((el) => !IDsSubcategoriesFromState.includes(el.id))
					.map((el) => ({
						id: el.id,
						name: el.name,
						isChanged: false,
						dishes: el.dishes.map((dish) => ({ ...dish, isAddedDishFromList: false })),
					}));
				dispatch({
					type: DishAssignmentActionTypes.SET_IDS_SELECTED_SUBCATEGORIES,
					payload: {
						IDsSelectedSubcategories: value,
						dishes: [...filteredDishesFromState, ...filteredDishesFromResponse],
					},
				});
			} else {
				// just to open and close tabs with dishes.
				dispatch({
					type: DishAssignmentActionTypes.SET_IDS_SELECTED_SUBCATEGORIES,
					payload: {
						IDsSelectedSubcategories: value,
						dishes: state.dishes,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const addSelectedDishesToSubcategory = () => {
		const selectedDishesFromDishList = state.IDsSelectedDishes.reduce<IDishWithAdditionalField[]>((acc, id) => {
			const searchDish = state.dishesForAssign.dishes.find((el) => el.id === id);
			if (searchDish) {
				acc.push({
					id: searchDish.id,
					name: searchDish.name,
					filePath: searchDish.filePath,
					categories: searchDish.categories.map((el) => (el.name ? el.name : 'no category name')),
					subCategories: searchDish.subCategories.map((el) =>
						el.subCategoryName ? el.subCategoryName : 'no subcategory name',
					),
					price: searchDish.price,
					isAddedDishFromList: true,
					externalId: searchDish.externalId,
				});
			}
			return acc;
		}, []);

		const IDsOfOpenSubcategoryTabs = Object.keys(state.IDsSelectedSubcategories).filter(
			(id) => state.IDsSelectedSubcategories[id] === true,
		);

		const updatedDishes = state.dishes.map<IDishesWithAdditionalProperty>((subcategory) => {
			if (!IDsOfOpenSubcategoryTabs.includes(subcategory.id)) {
				return subcategory;
			}
			const IDsOfAvailableDishes = subcategory.dishes.map((dish) => dish.id);
			const dishesForAdding = selectedDishesFromDishList.filter(
				(dish) => !IDsOfAvailableDishes.includes(dish.id),
			);
			return {
				...subcategory,
				isChanged: true,
				dishes: [...subcategory.dishes, ...dishesForAdding],
			};
		});

		dispatch({
			type: DishAssignmentActionTypes.SET_DISHES,
			payload: {
				dishes: updatedDishes,
				IDsSelectedDishes: [],
			},
		});
	};

	const deleteDishFromSubcategory = (subcategoryID: string, dishID: string) => {
		const updatedDishes = state.dishes.reduce<IDishesWithAdditionalProperty[]>((acc, subcategory) => {
			if (subcategory.id === subcategoryID) {
				const filteredDishes = subcategory.dishes.filter((el) => el.id !== dishID);
				acc.push({
					id: subcategory.id,
					name: subcategory.name,
					isChanged: true,
					dishes: filteredDishes,
				});
				return acc;
			}
			acc.push(subcategory);
			return acc;
		}, []);
		dispatch({
			type: DishAssignmentActionTypes.SET_DISHES,
			payload: {
				dishes: updatedDishes,
				IDsSelectedDishes: state.IDsSelectedDishes,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeIsShowModalWindowToCreateSubcategory = (value: boolean) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_IS_SHOW_MODAL_WINDOW_TO_CREATE_SUBCATEGORY,
			payload: {
				isShowModalWindowToCreateSubcategory: value,
			},
		});
	};

	const resetChanges = () => dispatch({ type: DishAssignmentActionTypes.RESET_CHANGES });

	const sendChanges = async () => {
		try {
			const restaurantIds = [
				state.IDSelectedRestaurant ? state.IDSelectedRestaurant : '',
				...state.selectedAdditionalRestaurants,
			];
			const assignments = state.dishes.reduce<IAssignmentsForAssignDish[]>((prevVal, currVal) => {
				if (currVal.isChanged) {
					prevVal.push({
						subCategoryId: currVal.id,
						dishIds: currVal.dishes.map((el) => el.id),
					});
					return prevVal;
				}
				return prevVal;
			}, []);
			showLoader();
			await assignDish({ restaurantIds, assignments });
			changeTypeOfModalWindow('success');
		} catch (err) {
			changeTypeOfModalWindow('error');
		} finally {
			hideLoader();
		}
	};

	const updateModuleDataAfterSaving = () => {
		resetChanges();
		refetch();
		setCategories(state.IDSelectedRestaurant ? state.IDSelectedRestaurant : '');
	};

	const updateCollectionOfCategories = () => {
		setCategories(state.IDSelectedRestaurant ? state.IDSelectedRestaurant : '');
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisible: value,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: DishAssignmentActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: string[]) => {
		dispatch({
			type: DishAssignmentActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				additionalRestaurants: value,
			},
		});
	};

	const changeSelectSearchString = (value: string) => {
		dispatch({
			type: DishAssignmentActionTypes.CHANGE_SELECT_SEARCH_STRING,
			payload: {
				selectSearchString: value,
			},
		});
	};

	useEffect(() => {
		if (state.IDSelectedRestaurant) {
			refetch();
		} else {
			dispatch({
				type: DishAssignmentActionTypes.SET_DISHES_FOR_ASSIGN,
				payload: {
					dishesForAssign: { dishesCount: 0, dishes: [] },
				},
			});
		}
	}, [
		state.currentPage,
		state.countOfItems,
		state.searchString,
		state.IDSelectedCustomer,
		state.IDSelectedRestaurant,
		state.price,
		state.isUnassignedDishes,
		state.sortBy,
	]);

	useEffect(() => {
		if (state.IDSelectedRestaurant) {
			setCategories(state.IDSelectedRestaurant);
		} else {
			dispatch({
				type: DishAssignmentActionTypes.SET_CATEGORIES,
				payload: {
					categories: [],
					notificationsForCategory: 'showNothing',
				},
			});
		}
	}, [state.IDSelectedRestaurant]);

	useEffect(() => {
		if (state.activeItemToDisplay === 'dishes' && !state.dishes.length) {
			changeActiveItemToDisplay('subCategories');
		} else if (!state.subcategories.length) {
			changeActiveItemToDisplay('categories');
		}
	}, [state.subcategories, state.dishes]);

	return (
		<DishAssignmentContext.Provider
			value={{
				changeSearchString,
				changeSearchStringForCategories,
				changeSearchStringForSubcategories,
				changeSearchStringForDishes,
				setIDSelectedCustomer,
				setFetchedRestaurants,
				setIDSelectedRestaurant,
				setPrice,
				resetSelectedControls,
				changeIsUnassignedDishesStatus,
				setSortBy,
				setIDsSelectedDishes,
				changeCurrentPage,
				setIDsSelectedCategories,
				setIDsSelectedSubcategories,
				changeActiveItemToDisplay,
				setShowBreadcrumbs,
				addSelectedDishesToSubcategory,
				deleteDishFromSubcategory,
				changeTypeOfModalWindow,
				changeIsShowModalWindowToCreateSubcategory,
				resetChanges,
				sendChanges,
				updateModuleDataAfterSaving,
				updateCollectionOfCategories,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeSelectSearchString,
				dishesForAssign: state.dishesForAssign,
				searchString: state.searchString,
				searchStringForCategories: state.searchStringForCategories,
				searchStringForSubcategories: state.searchStringForSubcategories,
				searchStringForDishes: state.searchStringForDishes,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				price: state.price,
				isUnassignedDishes: state.isUnassignedDishes,
				sortBy: state.sortBy,
				IDsSelectedDishes: state.IDsSelectedDishes,
				categories: state.categories,
				IDsSelectedCategories: state.IDsSelectedCategories,
				activeItemToDisplay: state.activeItemToDisplay,
				isShowBreadcrumbs: state.isShowBreadcrumbs,
				notificationsForCategory: state.notificationsForCategory,
				subcategories: state.subcategories,
				IDsSelectedSubcategories: state.IDsSelectedSubcategories,
				dishes: state.dishes,
				typeOfModalWindow: state.typeOfModalWindow,
				isShowModalWindowToCreateSubcategory: state.isShowModalWindowToCreateSubcategory,
				currentPage: state.currentPage,
				countOfItems: state.countOfItems,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				// below is the data for the modal window with additional restaurants
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				selectSearchString: state.selectSearchString,
			}}>
			{children}
		</DishAssignmentContext.Provider>
	);
};

export default DishAssignmentState;
